import moment from 'moment';

/**
 * Helpers for joining two server-side monthly aggregate series into a single
 * x-axis. Each aggregate row carries a `group` label (`YYYY-MM`) and one or
 * more numeric metrics. The dashboard plots income vs expense, paid vs
 * outstanding etc. over the same months — keeping the merge here means
 * the chart wrappers stay generic and the tests live in one file.
 */

export interface IMonthBucket {
  group?: string | null;
  total?: number | null;
}

export interface IInvoiceMonthBucket {
  group?: string | null;
  totalGross?: number | null;
  totalPaid?: number | null;
  totalOutstanding?: number | null;
}

const isYearMonth = (s: string) => /^\d{4}-\d{2}$/.test(s);

/**
 * Build a chronologically sorted union of month keys present in any series.
 * Skips empty/unknown labels so the x-axis stays well-formed.
 */
export const unionMonthKeys = (
  ...seriesGroups: { group?: string | null }[][]
): string[] => {
  const seen = new Set<string>();
  seriesGroups.forEach((rows) =>
    rows.forEach((r) => {
      if (r.group && isYearMonth(r.group)) seen.add(r.group);
    }),
  );
  return Array.from(seen).sort(
    (a, b) => moment(a).valueOf() - moment(b).valueOf(),
  );
};

const indexByGroup = <T extends { group?: string | null }>(rows: T[]) => {
  const map = new Map<string, T>();
  rows.forEach((r) => {
    if (r.group) map.set(r.group, r);
  });
  return map;
};

/** Align two `total`-style expense/revenue series to the same labels. */
export const alignMonthlySeries = (
  a: IMonthBucket[],
  b: IMonthBucket[],
): { labels: string[]; aValues: number[]; bValues: number[] } => {
  const labels = unionMonthKeys(a, b);
  const aIndex = indexByGroup(a);
  const bIndex = indexByGroup(b);
  return {
    labels,
    aValues: labels.map((m) => aIndex.get(m)?.total || 0),
    bValues: labels.map((m) => bIndex.get(m)?.total || 0),
  };
};

/** Per-month diff (a − b) — used for the Net Profit line. */
export const monthlyDiff = (
  a: IMonthBucket[],
  b: IMonthBucket[],
): { labels: string[]; values: number[] } => {
  const { labels, aValues, bValues } = alignMonthlySeries(a, b);
  return {
    labels,
    values: labels.map((_, i) => aValues[i] - bValues[i]),
  };
};

/** Project an invoice aggregate row set onto a two-series chart. */
export const splitInvoiceSeries = (
  rows: IInvoiceMonthBucket[],
  fieldA: keyof Pick<
    IInvoiceMonthBucket,
    'totalGross' | 'totalPaid' | 'totalOutstanding'
  >,
  fieldB: keyof Pick<
    IInvoiceMonthBucket,
    'totalGross' | 'totalPaid' | 'totalOutstanding'
  >,
): { labels: string[]; aValues: number[]; bValues: number[] } => {
  const labels = unionMonthKeys(rows);
  const index = indexByGroup(rows);
  return {
    labels,
    aValues: labels.map((m) => index.get(m)?.[fieldA] || 0),
    bValues: labels.map((m) => index.get(m)?.[fieldB] || 0),
  };
};
